// Angular
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// RxJS
import { Subscription, timer } from 'rxjs';

/**
 * Handle for work scheduled by {@link scheduleDelayed}. Call {@link DelayedHandle.cancel}
 * to abort before the callback runs (e.g. hover-hide cancelled by re-enter).
 */
export interface DelayedHandle {
  cancel(): void;
}

/**
 * Schedules `work` after `delayMs` via RxJS `timer`, with `DestroyRef` teardown
 * and a cancel handle. The RxJS async scheduler is Zone-patched like `setTimeout`,
 * so this does not by itself decouple the callback from Zone — zoneless-readiness
 * comes from `work` writing signals, navigating, or calling `markForCheck`.
 *
 * The subscription is always torn down with `destroyRef` so a late timer cannot
 * run after the host is destroyed.
 *
 * @param delayMs Delay in milliseconds before `work` runs.
 * @param work Callback to run once. Should update signals or otherwise schedule CD.
 * @param destroyRef Host destroyer; cancels the timer on teardown.
 */
export function scheduleDelayed(
  delayMs: number,
  work: () => void,
  destroyRef: DestroyRef,
): DelayedHandle {
  const subscription: Subscription = timer(delayMs)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() => work());

  return {
    cancel(): void {
      subscription.unsubscribe();
    },
  };
}
